// Admin-only: who holds calendar time and does not fly it.
//
// The point of this page is protecting availability for everyone else, so it
// leads with hours held versus hours flown rather than a no-show count. A
// renter who books four hours every weekend and flies one blocks the calendar
// just as effectively as a renter who never shows, and a count alone would rate
// them clean.
//
// Nothing here is visible to a lessee, and nothing here is a charge. Booking
// and flight records are joined by inference (see lib/utilization.ts), so these
// are prompts to go look, not findings.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { AlertTriangle, ChevronDown, Download, Search } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { DEMO_BOOKINGS, DEMO_FLIGHTS, DEMO_LESSEES } from '../../lib/demoData';
import type { BookingDoc, FlightDoc, UserDoc } from '../../lib/types';
import {
  byRenter,
  resolveBookings,
  LATE_CANCEL_HOURS,
  OUTCOME_BADGE,
  OUTCOME_LABEL,
  type RenterRow,
} from '../../lib/utilization';

type Window = 90 | 180 | 365 | 0;
const WINDOWS: { v: Window; label: string }[] = [
  { v: 90, label: 'Last 90 days' },
  { v: 180, label: 'Last 6 months' },
  { v: 365, label: 'Last year' },
  { v: 0, label: 'All time' },
];

const hrs = (n: number) => `${n.toFixed(1)} h`;
const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);
const fmtDay = (d: Date) =>
  d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
/** Compact date for the inline lists — the year only when it isn't this one. */
const fmtShort = (d: Date) =>
  d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
const dateList = (dates: Date[], max = 6) =>
  dates.slice(0, max).map(fmtShort).join(' · ') + (dates.length > max ? ` +${dates.length - max} more` : '');

/** Row label in the fact list — uppercase, muted, never bold. */
function Dt({ children }: { children: React.ReactNode }) {
  return <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle sm:pt-0.5">{children}</dt>;
}

/** A stat that is also the filter for itself — tap No-shows, see only no-shows. */
function Stat({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: string;
  tone?: 'warn';
  active?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div
        className={`mt-0.5 font-display text-lg font-semibold sm:text-xl ${
          tone === 'warn' ? 'text-[#B42318]' : 'text-navy-900'
        }`}
      >
        {value}
      </div>
    </>
  );
  if (!onClick) return <div className="card p-3 text-center sm:p-4">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card p-3 text-center transition sm:p-4 ${
        active ? 'ring-2 ring-brand' : 'hover:bg-sky-50'
      }`}
    >
      {body}
    </button>
  );
}

export function AdminUtilization() {
  const { demoMode } = useAuth();
  const [bookings, setBookings] = useState<BookingDoc[] | null>(null);
  const [flights, setFlights] = useState<FlightDoc[] | null>(null);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [window, setWindow] = useState<Window>(90);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  // Counting no-shows and late cancels separately is only half the answer — you
  // also need to see just one of them. The stat tiles double as these filters.
  const [only, setOnly] = useState<'all' | 'no_show' | 'late_cancel' | 'unclassified'>('all');

  useEffect(() => {
    if (demoMode) {
      setBookings(DEMO_BOOKINGS);
      setFlights(DEMO_FLIGHTS);
      setUsers(DEMO_LESSEES);
      return;
    }
    if (!db) return;
    const unsubB = onSnapshot(collection(db, 'bookings'), (snap) =>
      setBookings(snap.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id }))),
    );
    const unsubF = onSnapshot(query(collection(db, 'flights'), orderBy('date', 'desc')), (snap) =>
      setFlights(snap.docs.map((d) => ({ ...(d.data() as FlightDoc), flightId: d.id }))),
    );
    const unsubU = onSnapshot(collection(db, 'users'), (snap) =>
      setUsers(snap.docs.map((d) => ({ ...(d.data() as UserDoc), uid: d.id }))),
    );
    return () => {
      unsubB();
      unsubF();
      unsubU();
    };
  }, [demoMode]);

  const adminUids = useMemo(
    () => new Set(users.filter((u) => u.role === 'admin').map((u) => u.uid)),
    [users],
  );

  const rows = useMemo<RenterRow[] | null>(() => {
    if (!bookings || !flights) return null;
    const cutoff = window === 0 ? 0 : Date.now() - window * 86400000;

    const scoped = bookings.filter((b) => {
      // Owner maintenance holds are not renter behavior.
      if (b.maintenanceLabel) return false;
      if (adminUids.has(b.lesseeUid)) return false;
      return b.startDateTime.toDate().getTime() >= cutoff;
    });

    return byRenter(resolveBookings(scoped, flights), users);
  }, [bookings, flights, users, adminUids, window]);

  const shown = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    let list = rows.filter((r) => r.accountable > 0);
    if (only === 'no_show') list = list.filter((r) => r.noShows > 0);
    else if (only === 'late_cancel') list = list.filter((r) => r.lateCancels > 0);
    else if (only === 'unclassified') list = list.filter((r) => r.cancelledUnknown > 0);
    if (!q) return list;
    return list.filter(
      (r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
  }, [rows, search, only]);

  // Totals stay on the full population so the tiles don't change when you filter
  // by them — a filter that rewrites its own counter is useless as a filter.
  const population = useMemo(() => (rows ?? []).filter((r) => r.accountable > 0), [rows]);

  const totals = useMemo(() => {
    if (!rows) return null;
    const noShows = population.reduce((s, r) => s + r.noShows, 0);
    const lateCancels = population.reduce((s, r) => s + r.lateCancels, 0);
    const unclassified = population.reduce((s, r) => s + r.cancelledUnknown, 0);
    const held = population.reduce((s, r) => s + r.heldHours, 0);
    const flown = population.reduce((s, r) => s + r.flownHours, 0);
    return { noShows, lateCancels, unclassified, unflown: Math.max(0, held - flown), held, flown };
  }, [rows, population]);

  function exportCsv() {
    if (!shown) return;
    const iso = (dates: Date[]) => dates.map((d) => d.toISOString().slice(0, 10)).join(' ');
    const head = [
      'Renter', 'Email', 'Bookings', 'Flew', 'No-shows', 'No-show dates',
      'Late cancels', 'Late cancel dates', 'Released in time', 'Released dates',
      'Hours held', 'Hours flown', 'Utilization', 'Unused rate',
    ];
    const lines = shown.map((r) =>
      [
        r.name, r.email, r.accountable, r.flew,
        r.noShows, iso(r.noShowDates),
        r.lateCancels, iso(r.lateCancelDates),
        r.released, iso(r.releasedDates),
        r.heldHours.toFixed(1), r.flownHours.toFixed(1), pct(r.utilization), pct(r.wasteRate),
      ]
        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
        .join(','),
    );
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ajm-utilization-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <>
      <PageHeader
        title="Booking utilization"
        subtitle="Who holds calendar time and doesn't fly it. Admin only."
        actions={
          shown?.length ? (
            <button onClick={exportCsv} className="btn-secondary inline-flex items-center gap-1.5 text-sm">
              <Download size={14} /> Export CSV
            </button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w.v}
            onClick={() => setWindow(w.v)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              window === w.v ? 'bg-brand text-white' : 'bg-slate-100 text-ink-muted hover:bg-slate-200'
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {!shown || !totals ? (
        <Spinner label="Reading bookings and flights" />
      ) : shown.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">
          No completed bookings in this window.
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-3 gap-3">
            <Stat
              label="No-shows"
              value={String(totals.noShows)}
              tone={totals.noShows ? 'warn' : undefined}
              active={only === 'no_show'}
              onClick={() => setOnly(only === 'no_show' ? 'all' : 'no_show')}
            />
            <Stat
              label="Late cancels"
              value={String(totals.lateCancels)}
              tone={totals.lateCancels ? 'warn' : undefined}
              active={only === 'late_cancel'}
              onClick={() => setOnly(only === 'late_cancel' ? 'all' : 'late_cancel')}
            />
            <Stat label="Hours held, not flown" value={hrs(totals.unflown)} />
          </div>

          {(only !== 'all' || totals.unclassified > 0) && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
              {only !== 'all' && (
                <>
                  <span className="text-ink-muted">
                    Showing renters with{' '}
                    {only === 'no_show'
                      ? 'a no-show'
                      : only === 'late_cancel'
                        ? 'a late cancel'
                        : 'an unclassified cancellation'}
                  </span>
                  <button
                    onClick={() => setOnly('all')}
                    className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-ink-muted hover:bg-slate-200"
                  >
                    Clear
                  </button>
                </>
              )}
              {totals.unclassified > 0 && only !== 'unclassified' && (
                <button
                  onClick={() => setOnly('unclassified')}
                  className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-ink-muted hover:bg-slate-200"
                >
                  {totals.unclassified} cancellation{totals.unclassified > 1 ? 's' : ''} from before
                  cancel times were recorded
                </button>
              )}
            </div>
          )}

          <div className="relative mb-4">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle"
            />
            <input
              className="input pl-9"
              placeholder="Search by renter name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="space-y-3">
            {shown.map((r) => {
              const open = expanded === r.uid;
              const wasteful = r.noShows + r.lateCancels > 0;
              return (
                <div key={r.uid} className="card overflow-hidden p-0">
                  {r.noShows >= 3 && (
                    <div className="flex items-center gap-2 bg-[#B42318] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white">
                      <AlertTriangle size={13} className="shrink-0" />
                      {r.noShows} no-shows in this window
                    </div>
                  )}

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <Link to={`/admin/lessees/${r.uid}`} className="group min-w-0">
                        <div className="font-medium text-navy-900 group-hover:underline">{r.name}</div>
                        <div className="truncate text-xs text-ink-muted">{r.email}</div>
                      </Link>
                      <span className="shrink-0 whitespace-nowrap font-display text-lg font-semibold text-navy-900">
                        {pct(r.utilization)}
                        <span className="ml-1 text-[11px] font-normal uppercase tracking-wider text-ink-subtle">
                          flown
                        </span>
                      </span>
                    </div>

                    <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[128px_1fr]">
                      <Dt>Bookings</Dt>
                      <dd className="text-ink">
                        {r.accountable} · {r.flew} flown
                      </dd>

                      {/* Dates, not just counts — "3 no-shows" is a number, but
                          "Aug 2 · Aug 16 · Aug 30" is a pattern you can act on. */}
                      <Dt>No-shows</Dt>
                      <dd className={r.noShows ? 'text-[#B42318]' : 'text-ink-muted'}>
                        {r.noShows ? `${r.noShows} — ${dateList(r.noShowDates)}` : 'None'}
                      </dd>

                      <Dt>Late cancels</Dt>
                      <dd className={r.lateCancels ? 'text-[#B42318]' : 'text-ink-muted'}>
                        {r.lateCancels ? `${r.lateCancels} — ${dateList(r.lateCancelDates)}` : 'None'}
                      </dd>

                      <Dt>Released</Dt>
                      <dd className={r.released ? 'text-ink' : 'text-ink-muted'}>
                        {r.released ? `${r.released} with notice — ${dateList(r.releasedDates)}` : 'None'}
                      </dd>

                      <Dt>Hours</Dt>
                      <dd className="text-ink">
                        {hrs(r.flownHours)} flown of {hrs(r.heldHours)} held
                      </dd>

                      {r.cancelledUnknown > 0 && (
                        <>
                          <Dt>Unclassified</Dt>
                          <dd className="text-ink-muted">
                            {r.cancelledUnknown} cancelled before cancellation times were recorded
                          </dd>
                        </>
                      )}
                    </dl>
                  </div>

                  <button
                    onClick={() => setExpanded(open ? null : r.uid)}
                    className="flex w-full items-center gap-1.5 border-t border-slate-100 px-4 py-3 text-xs font-semibold text-navy-900 hover:bg-sky-50"
                  >
                    <ChevronDown size={14} className={open ? 'rotate-180 transition' : 'transition'} />
                    {open ? 'Hide bookings' : `Show all ${r.resolved.length} bookings`}
                  </button>

                  {open && (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 bg-sky-50/40">
                      {r.resolved.map((res) => (
                        <div
                          key={res.booking.bookingId}
                          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
                        >
                          <span className="text-ink">{fmtDay(res.start)}</span>
                          <span className="text-xs text-ink-muted">
                            {hrs(res.heldHours)} held
                            {res.flownHours > 0 && ` · ${hrs(res.flownHours)} flown`}
                          </span>
                          <span className={`${OUTCOME_BADGE[res.outcome]} shrink-0 whitespace-nowrap`}>
                            {OUTCOME_LABEL[res.outcome]}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-6 text-xs leading-relaxed text-ink-muted">
            A booking counts as flown when that renter logged a flight on a date the booking covered —
            flights don&rsquo;t record which booking they belong to, so this is inferred. A cancellation
            inside {LATE_CANCEL_HOURS} hours of the start is a late cancel; earlier than that, the slot went
            back on the calendar in time. Bookings AJM stood down for weather or maintenance, and owner
            maintenance holds, are left out.
          </p>
        </>
      )}
    </>
  );
}
